import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ErroApi } from '../../nucleo/api';
import { textoDoErro, textoDoItem } from '../../nucleo/erros';
import { BOTAO_PRIMARIO } from './tipos';

/** SB-01: texto de um erro da API no idioma da tela, com os códigos próprios do Treinamento (namespace 'treinamento'). */
export const textoErro = (e: unknown) => textoDoErro(e, 'treinamento');

/** SB-01: erros por campo do formulário no idioma da tela (como errosPorCampo do núcleo, mas traduzindo também os
 * códigos próprios do Treinamento). `mapa` liga o código do erro ao campo; o que não tem campo vai para `geral`. */
export function errosDoForm(e: unknown, mapa: Record<string, string>): { campos: Record<string, string>; geral: string | null } {
  if (!e) return { campos: {}, geral: null };
  if (!(e instanceof ErroApi)) return { campos: {}, geral: textoErro(e) };
  const todos = e.erros.length ? e.erros : [{ code: e.code, message: e.message, details: e.details }];
  const campos: Record<string, string> = {};
  const restantes: string[] = [];
  for (const x of todos) {
    const campo = mapa[x.code];
    const texto = textoDoItem(x, 'treinamento');
    if (campo && !campos[campo]) campos[campo] = texto;
    else if (!restantes.includes(texto)) restantes.push(texto);
  }
  return { campos, geral: restantes.length ? restantes.join(' ') : null };
}

/** Selo colorido (status). Cor só por classe de token. */
export function Selo({ texto, cor }: { texto: string; cor: string }) {
  return <span className={`inline-flex shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${cor}`}>{texto}</span>;
}

/** Barra de progresso do curso (largura por estilo, cor por token). */
export function Barra({ percentual, rotulo }: { percentual: number; rotulo?: string }) {
  return (
    <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentual} aria-label={rotulo} className="h-1.5 w-full overflow-hidden rounded-full bg-sup-3">
      <div className={`h-full ${percentual >= 100 ? 'bg-sucesso' : 'bg-acento'}`} style={{ width: `${Math.min(100, Math.max(0, percentual))}%` }} />
    </div>
  );
}

/** Bloco com kicker e ação opcional. */
export function Bloco({ titulo, acao, children }: { titulo: string; acao?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-xl border border-borda bg-sup-1 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="kicker">{titulo}</h2>
        {acao}
      </div>
      {children}
    </section>
  );
}

/** Botão primário da aba que abre o formulário pela URL (?novo=1). */
export function AcaoNovo({ rotulo }: { rotulo: string }) {
  const { t } = useTranslation('treinamento');
  const [params, setParams] = useSearchParams();
  return (
    <button type="button" onClick={() => setParams({ ...Object.fromEntries(params), novo: '1' })} className={BOTAO_PRIMARIO}>
      {t(rotulo)}
    </button>
  );
}

export function useFecharNovo() {
  const [params, setParams] = useSearchParams();
  return {
    aberto: params.get('novo') === '1',
    fechar: () => {
      params.delete('novo');
      setParams(params, { replace: true });
    },
  };
}

/** Minutos legíveis pelo dicionário (sem número fracionado). */
export function useDuracao() {
  const { t } = useTranslation('treinamento');
  return (min: number) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h === 0 ? t('duracao.min', { m }) : m === 0 ? t('duracao.h', { h }) : t('duracao.hm', { h, m });
  };
}
